import { createContext, useContext, useMemo, useState, useCallback } from 'react';

const DEFAULT_FILTERS = {
  keyword: '',
  technicianTypeId: '',
  city: '',
  state: '',
  maxHourlyRate: '',
  minRating: '',
  availableOnly: true,
  sortBy: 'averageRating',
  sortDirection: 'DESC',
  page: 0,
  size: 12,
};

const FilterContext = createContext(null);

/** Holds the technician-search filter state shared between the filter bar and the results list. */
export function FilterProvider({ children }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const update = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch, page: 'page' in patch ? patch.page : 0 }));
  }, []);

  const setPage = useCallback((page) => setFilters((prev) => ({ ...prev, page })), []);
  const reset = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  /** Strips empty strings so they aren't sent as query params / body fields. */
  const asQuery = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  }, [filters]);

  const value = useMemo(
    () => ({ filters, asQuery, update, setPage, reset }),
    [filters, asQuery, update, setPage, reset]
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

export function useFilters() {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within <FilterProvider>');
  return ctx;
}

export { DEFAULT_FILTERS };
export default FilterContext;
